/**
 * CryptoPaymentReown - Multi-chain crypto payment
 * Simplified flow: QR codes first, wallet connect only for EVM chains
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAppKit, useAppKitAccount, useAppKitProvider } from '@reown/appkit/react';
import { BrowserProvider, parseUnits, Contract } from 'ethers';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Wallet, CheckCircle, Loader2, ExternalLink, X, ArrowLeft, 
  Copy, Check, AlertTriangle, Coins, QrCode
} from 'lucide-react';
import { 
  MERCHANT_WALLETS, TOKEN_CONTRACTS, CHAIN_NAMES, NETWORK_INFO 
} from '../config/reown.config';

// ERC20 ABI for token transfers
const ERC20_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function balanceOf(address account) view returns (uint256)',
  'function decimals() view returns (uint8)',
];

// All supported networks
const ALL_NETWORKS = [
  { id: 'bitcoin', name: 'Bitcoin', icon: '₿', token: 'BTC', isEVM: false, hasWalletConnect: false },
  { id: 'solana', name: 'Solana', icon: '◎', token: 'SOL', isEVM: false, hasWalletConnect: true },
  { id: 'tron', name: 'Tron', icon: '⚡', token: 'USDT', isEVM: false, hasWalletConnect: false },
  { id: 'polygon', name: 'Polygon', icon: '🟣', token: 'USDC', isEVM: true, hasWalletConnect: true, recommended: true, chainId: 137 },
  { id: 'base', name: 'Base', icon: '🔵', token: 'USDC', isEVM: true, hasWalletConnect: true, chainId: 8453 },
  { id: 'arbitrum', name: 'Arbitrum', icon: '🔷', token: 'USDC', isEVM: true, hasWalletConnect: true, chainId: 42161 },
  { id: 'ethereum', name: 'Ethereum', icon: '⟠', token: 'USDC', isEVM: true, hasWalletConnect: true, chainId: 1 },
];

const CryptoPaymentReown = ({ 
  amount, 
  onSuccess, 
  onCancel, 
  bookingDetails, 
  guestEmail, 
  guestName 
}) => {
  const { open } = useAppKit();
  const { address, isConnected, chainId } = useAppKitAccount();
  const { walletProvider } = useAppKitProvider('eip155');
  
  const [step, setStep] = useState('warning');
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [copied, setCopied] = useState(false);
  const [merchantWallets, setMerchantWallets] = useState(MERCHANT_WALLETS);
  
  // Auto-detection state
  const [autoChecking, setAutoChecking] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState(null);
  const autoCheckIntervalRef = useRef(null);

  const currentChainName = chainId ? CHAIN_NAMES[chainId] : null;
  const selectedNetworkInfo = ALL_NETWORKS.find(n => n.id === selectedNetwork);

  // Fetch merchant wallets from API
  useEffect(() => {
    const fetchWallets = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${API}/api/crypto/wallets`);
        if (res.data) {
          setMerchantWallets(res.data);
        }
      } catch (err) {
        console.log('Using default wallet addresses');
      }
    };
    fetchWallets();
  }, []);

  const copyAddress = async (addr) => {
    try {
      await navigator.clipboard.writeText(addr);
      setCopied(true);
      toast.success('Address copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Handle EVM token transfer using connected wallet
  const handleEVMPayment = async () => {
    if (!isConnected || !walletProvider) {
      toast.error('Please connect your wallet first');
      open();
      return;
    }

    setProcessing(true);

    try {
      const chainName = CHAIN_NAMES[chainId];
      if (!chainName || chainName !== selectedNetwork) {
        toast.error(`Please switch to ${selectedNetworkInfo?.name} in your wallet`);
        setProcessing(false);
        return;
      }

      const provider = new BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      
      const tokenAddress = TOKEN_CONTRACTS[chainName]?.[selectedToken];
      if (!tokenAddress) {
        toast.error(`${selectedToken} not supported on ${chainName}`);
        setProcessing(false);
        return;
      }

      const merchantWallet = merchantWallets[chainName];
      const tokenContract = new Contract(tokenAddress, ERC20_ABI, signer);
      
      const decimals = await tokenContract.decimals();
      const tokenAmount = parseUnits(amount.toString(), decimals);

      toast.info('Please confirm the transaction in your wallet...');

      const tx = await tokenContract.transfer(merchantWallet, tokenAmount);
      setTxHash(tx.hash);
      
      toast.info('Transaction submitted, waiting for confirmation...');

      const receipt = await tx.wait();

      if (receipt.status === 1) {
        setStep('success');
        toast.success('Payment successful!');
        
        if (onSuccess) {
          onSuccess({
            txHash: tx.hash,
            network: chainName,
            token: selectedToken,
            amount,
            from: address,
            to: merchantWallet,
          });
        }
      } else {
        throw new Error('Transaction failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      
      if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
        toast.error('Transaction cancelled');
      } else {
        toast.error(error.shortMessage || error.message || 'Payment failed');
      }
    } finally {
      setProcessing(false);
    }
  };

  // Register pending payment for auto-detection
  const confirmPaymentSent = async () => {
    try {
      const sessionId = `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setPaymentSessionId(sessionId);
      
      const API = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API}/api/crypto/register-pending`, {
        session_id: sessionId,
        expected_amount: amount,
        network: selectedNetwork,
        guest_email: guestEmail || '',
        guest_name: guestName || '',
        room_type: bookingDetails?.room_type || '',
        check_in_date: bookingDetails?.check_in_date || '',
        check_out_date: bookingDetails?.check_out_date || '',
        nights: bookingDetails?.nights || 1,
      });
      
      setAutoChecking(true);
      setStep('waiting-confirmation');
      
      let checkCount = 0;
      const maxChecks = 120;
      
      autoCheckIntervalRef.current = setInterval(async () => {
        checkCount++;
        
        if (checkCount >= maxChecks) {
          clearInterval(autoCheckIntervalRef.current);
          setAutoChecking(false);
          toast.info('Payment detection timed out. Contact support with your transaction hash.');
          return;
        }
        
        try {
          const res = await axios.get(`${API}/api/crypto/check-payment/${sessionId}`);
          
          if (res.data.status === 'confirmed') {
            clearInterval(autoCheckIntervalRef.current);
            setAutoChecking(false);
            setTxHash(res.data.tx_hash);
            toast.success('Payment confirmed!');
            
            if (onSuccess) {
              onSuccess({
                txHash: res.data.tx_hash,
                network: selectedNetwork,
                token: selectedToken || 'CRYPTO',
                amount: res.data.amount,
                from: 'auto-detected',
                to: merchantWallets[selectedNetwork],
              });
            }
            setStep('success');
          }
        } catch (err) {
          console.log('Payment check polling:', err.message);
        }
      }, 15000);
      
    } catch (err) {
      console.error('Failed to register pending payment:', err);
      toast.success('Thank you! We will verify your payment shortly.');
      if (onSuccess) {
        onSuccess({
          txHash: 'pending-verification',
          network: selectedNetwork,
          token: selectedToken || 'CRYPTO',
          amount,
          from: 'external-wallet',
          to: MERCHANT_WALLETS[selectedNetwork],
        });
      }
      setStep('success');
    }
  };

  useEffect(() => {
    return () => {
      if (autoCheckIntervalRef.current) {
        clearInterval(autoCheckIntervalRef.current);
      }
    };
  }, []);

  const getExplorerUrl = () => {
    const explorers = {
      polygon: 'https://polygonscan.com/tx/',
      base: 'https://basescan.org/tx/',
      arbitrum: 'https://arbiscan.io/tx/',
      ethereum: 'https://etherscan.io/tx/',
      bitcoin: 'https://mempool.space/tx/',
      solana: 'https://solscan.io/tx/',
      tron: 'https://tronscan.org/#/transaction/',
    };
    return txHash && txHash !== 'pending-verification' 
      ? `${explorers[selectedNetwork]}${txHash}` 
      : '';
  };

  const getQRValue = () => {
    const addr = merchantWallets[selectedNetwork];
    if (selectedNetwork === 'bitcoin') {
      return `bitcoin:${addr}?amount=${amount}`;
    }
    if (selectedNetwork === 'solana') {
      return `solana:${addr}?amount=${amount}&label=Hodler%20Inn`;
    }
    return addr;
  };

  return (
    <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-slate-700 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
            <Wallet className="w-5 h-5 text-black" />
          </div>
          <div>
            <h2 className="text-white font-bold">Pay with Crypto</h2>
            <p className="text-amber-400 text-sm font-semibold">${amount} USD</p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-white p-2 hover:bg-slate-800 rounded-lg transition-colors"
          data-testid="crypto-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Step 0: Non-Refundable Warning */}
        {step === 'warning' && (
          <>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
              <h3 className="text-white font-bold text-lg mb-2">Non-Refundable Booking</h3>
              <p className="text-gray-300 text-sm mb-4">
                Cryptocurrency payments are <span className="text-amber-400 font-semibold">final and cannot be reversed</span>.
              </p>
              <div className="bg-black/30 rounded-lg p-3 text-left space-y-2">
                <p className="text-gray-400 text-sm flex items-start gap-2">
                  <span className="text-red-400">✗</span> No refunds or cancellations
                </p>
                <p className="text-gray-400 text-sm flex items-start gap-2">
                  <span className="text-red-400">✗</span> No chargebacks possible
                </p>
                <p className="text-gray-400 text-sm flex items-start gap-2">
                  <span className="text-amber-400">!</span> Book only if 100% sure
                </p>
              </div>
            </div>
            
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={agreedToPolicy}
                onChange={(e) => setAgreedToPolicy(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
                data-testid="agree-policy-checkbox"
              />
              <span className="text-gray-300 text-sm group-hover:text-white transition-colors">
                I understand this crypto payment is <span className="text-amber-400 font-semibold">non-refundable</span>.
              </span>
            </label>

            <div className="flex gap-3">
              <Button
                onClick={onCancel}
                variant="outline"
                className="flex-1 border-slate-600 text-gray-400 hover:text-white hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                onClick={() => setStep('select-network')}
                disabled={!agreedToPolicy}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold disabled:opacity-50"
                data-testid="agree-continue-btn"
              >
                Continue
              </Button>
            </div>
          </>
        )}

        {/* Step 1: Select Network */}
        {step === 'select-network' && (
          <>
            <p className="text-gray-400 text-sm text-center">Select your preferred payment method</p>
            
            <div className="grid grid-cols-2 gap-3">
              {ALL_NETWORKS.map((network) => (
                <button
                  key={network.id}
                  onClick={() => {
                    setSelectedNetwork(network.id);
                    setSelectedToken(network.token);
                    if (network.hasWalletConnect) {
                      setStep('wallet-options');
                    } else {
                      setStep('address-qr');
                    }
                  }}
                  className={`p-4 bg-slate-800 hover:bg-slate-700 border border-slate-600 hover:border-amber-500 rounded-xl transition-all text-center relative ${
                    network.recommended ? 'ring-1 ring-green-500/50' : ''
                  }`}
                  data-testid={`network-${network.id}-btn`}
                >
                  {network.recommended && (
                    <span className="absolute -top-2 -right-2 bg-green-500 text-black text-xs px-2 py-0.5 rounded-full font-medium">
                      Low Fee
                    </span>
                  )}
                  <span className="text-3xl block mb-2">{network.icon}</span>
                  <p className="text-white font-medium">{network.name}</p>
                  <p className="text-gray-500 text-xs">{network.token}</p>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Step 2: Wallet Options - Connect Wallet or QR */}
        {step === 'wallet-options' && selectedNetwork && (
          <>
            <button
              onClick={() => {
                setSelectedNetwork(null);
                setSelectedToken(null);
                setStep('select-network');
              }}
              className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="text-center">
              <span className="text-4xl">{selectedNetworkInfo?.icon}</span>
              <p className="text-white font-semibold mt-2">{selectedNetworkInfo?.name}</p>
              <p className="text-gray-400 text-sm">Send ${amount} in {selectedToken}</p>
            </div>

            {/* Option 1: Connect Wallet */}
            <button
              onClick={() => {
                if (!isConnected) {
                  open();
                } else if (selectedNetworkInfo?.isEVM) {
                  setStep('select-token');
                } else {
                  // For Solana, show QR after connecting (send not fully supported via dApp yet)
                  setStep('address-qr');
                  toast.info('Please send SOL using your connected wallet');
                }
              }}
              className="w-full p-4 bg-gradient-to-r from-orange-500/20 to-amber-500/20 hover:from-orange-500/30 hover:to-amber-500/30 border border-orange-500/30 hover:border-orange-500 rounded-xl transition-all text-left group"
              data-testid="connect-wallet-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center group-hover:bg-orange-500/30 transition-colors">
                  <Coins className="w-7 h-7 text-orange-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-semibold">
                    {isConnected ? 'Pay with Connected Wallet' : 'Connect Wallet'}
                  </p>
                  <p className="text-gray-400 text-sm">
                    {isConnected 
                      ? `${address?.slice(0, 6)}...${address?.slice(-4)}` 
                      : selectedNetwork === 'solana' 
                        ? 'Phantom, Solflare, Backpack...'
                        : 'MetaMask, Trust, Coinbase...'}
                  </p>
                </div>
              </div>
            </button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-700"></div>
              <span className="text-gray-500 text-xs">OR</span>
              <div className="flex-1 h-px bg-slate-700"></div>
            </div>

            {/* Option 2: QR Code */}
            <button
              onClick={() => setStep('address-qr')}
              className="w-full p-4 bg-slate-800 hover:bg-slate-700 border border-slate-600 hover:border-amber-500 rounded-xl transition-all text-left"
              data-testid="qr-option-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center">
                  <QrCode className="w-7 h-7 text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-semibold">Scan QR Code</p>
                  <p className="text-gray-400 text-sm">Copy address manually</p>
                </div>
              </div>
            </button>
          </>
        )}

        {/* Step 3: Select Token (for EVM wallet payment) */}
        {step === 'select-token' && selectedNetwork && (
          <>
            <button
              onClick={() => setStep('wallet-options')}
              className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            {isConnected && (
              <div className="bg-slate-800 rounded-lg p-3 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-gray-300 text-sm">
                  {address?.slice(0, 6)}...{address?.slice(-4)}
                </span>
                <button 
                  onClick={() => open()}
                  className="ml-auto text-amber-400 text-xs hover:underline"
                >
                  Switch
                </button>
              </div>
            )}

            <div className="flex items-center gap-2 text-white">
              <span className="text-xl">{selectedNetworkInfo?.icon}</span>
              <span className="font-medium">{selectedNetworkInfo?.name}</span>
              {currentChainName !== selectedNetwork && (
                <span className="text-amber-400 text-xs ml-auto">Switch network in wallet</span>
              )}
            </div>

            <p className="text-gray-400 text-sm">Select Token</p>
            <div className="flex gap-2">
              {Object.keys(TOKEN_CONTRACTS[selectedNetwork] || {}).map((token) => (
                <button
                  key={token}
                  onClick={() => setSelectedToken(token)}
                  className={`flex-1 p-3 rounded-xl border transition-all ${
                    selectedToken === token
                      ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                      : 'bg-slate-800 border-slate-600 text-white hover:border-slate-500'
                  }`}
                  data-testid={`token-${token}-btn`}
                >
                  <p className="font-bold">{token}</p>
                  <p className="text-xs text-gray-400">Stablecoin</p>
                </button>
              ))}
            </div>

            <Button
              onClick={handleEVMPayment}
              disabled={processing || !selectedToken}
              className="w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black font-bold mt-2"
              data-testid="pay-now-btn"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Wallet className="w-5 h-5 mr-2" />
                  Pay ${amount} {selectedToken}
                </>
              )}
            </Button>
            <p className="text-gray-500 text-xs text-center">
              {currentChainName === selectedNetwork 
                ? 'Confirm transaction in your wallet'
                : `You'll be prompted to switch to ${selectedNetworkInfo?.name}`
              }
            </p>
          </>
        )}

        {/* QR Code / Address View */}
        {step === 'address-qr' && selectedNetwork && (
          <>
            <button
              onClick={() => {
                if (selectedNetworkInfo?.hasWalletConnect) {
                  setStep('wallet-options');
                } else {
                  setSelectedNetwork(null);
                  setSelectedToken(null);
                  setStep('select-network');
                }
              }}
              className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="text-center">
              <span className="text-4xl">{selectedNetworkInfo?.icon}</span>
              <p className="text-white font-semibold mt-2">
                Send ${amount} in {selectedToken}
              </p>
              <p className="text-gray-400 text-sm">
                Scan QR or copy address below
              </p>
            </div>

            <div className="bg-white rounded-xl p-4 mx-auto w-fit">
              <QRCodeSVG value={getQRValue()} size={160} level="H" includeMargin />
            </div>

            <div className="bg-slate-800 rounded-xl p-3">
              <p className="text-gray-400 text-xs mb-1">
                {selectedNetwork.toUpperCase()} Address
              </p>
              <div className="flex items-center gap-2">
                <code className="text-amber-400 text-xs break-all flex-1 font-mono">
                  {merchantWallets[selectedNetwork]}
                </code>
                <button
                  onClick={() => copyAddress(merchantWallets[selectedNetwork])}
                  className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors flex-shrink-0"
                  data-testid="copy-address-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gray-400" />}
                </button>
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
              <p className="text-amber-400 text-sm text-center">
                After sending, click below - we'll auto-detect your payment!
              </p>
            </div>

            <Button
              onClick={confirmPaymentSent}
              className="w-full h-12 bg-green-600 hover:bg-green-500 text-white font-bold"
              data-testid="confirm-sent-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              I've Sent the Payment
            </Button>
          </>
        )}

        {/* Waiting for Payment Confirmation */}
        {step === 'waiting-confirmation' && (
          <div className="py-8 text-center space-y-4">
            <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto">
              <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
            </div>
            <p className="text-white font-bold text-xl">Detecting Payment...</p>
            <p className="text-gray-400 text-sm">
              Monitoring blockchain for your payment.<br />
              This may take a few minutes.
            </p>
            
            <div className="bg-slate-800 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Amount:</span>
                <span className="text-white font-medium">${amount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Network:</span>
                <span className="text-white font-medium capitalize">{selectedNetwork}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Status:</span>
                <span className="text-amber-400 font-medium flex items-center gap-1">
                  <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
                  Scanning...
                </span>
              </div>
            </div>
            
            <Button
              onClick={() => {
                if (autoCheckIntervalRef.current) {
                  clearInterval(autoCheckIntervalRef.current);
                }
                setAutoChecking(false);
                setStep('address-qr');
              }}
              variant="ghost"
              className="text-gray-400 hover:text-white text-sm"
            >
              Go Back
            </Button>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="py-8 text-center space-y-4">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-green-400" />
            </div>
            <p className="text-white font-bold text-xl">Payment Received!</p>
            <p className="text-gray-400 text-sm">
              Thank you! Your booking is being processed.
            </p>
            {getExplorerUrl() && (
              <a
                href={getExplorerUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-400 text-sm flex items-center justify-center gap-1 hover:underline"
              >
                View Transaction <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <Button
              onClick={onCancel}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white"
              data-testid="crypto-done-btn"
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CryptoPaymentReown;
